import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  countSession,
  needsLoadScan,
  plannedBoxes,
  precheckRunLoad,
  unscannedPlanned,
  type AddScanResult,
  type OpsSession,
} from '@ecobox/firebase';
import {
  Button,
  Card,
  EmptyState,
  ScanInput,
  ScannerView,
  scanFeedback,
  useToast,
  type ScanVerdict,
} from '@ecobox/ui';
import { ScanRows } from '../../components/ScanRows';
import { useDriverData } from '../../hooks/useDriver';
import { useRunBranch } from '../../hooks/useRunBranch';
import { useWakeLock } from '../../hooks/useWakeLock';
import { loadBarcodeDetector } from '../../scanner/detector';
import { useServices, useSession } from '../../services';
import { useAllSessions, useLedgerById, useSessionEngine } from '../../sessions';
import { BackLink, Tile } from './shared';

const VERDICT: Record<AddScanResult['tone'], ScanVerdict> = { ok: 'ok', warn: 'warn', bad: 'bad' };

/** The loading scan that starts a run (05 §5.2 Run): one `startRun` with the boxes that went on the van. */
export function RunLoadPage() {
  const { runId = '' } = useParams();
  const data = useDriverData();
  const { uid } = useSession();
  const sessions = useAllSessions();
  const run = data.runs.find((r) => r.id === runId);
  const session = sessions.find(
    (s) =>
      s.kind === 'run_load' && s.targetId === runId && s.uid === uid && s.finishedAt === undefined,
  );
  const sent = sessions.find(
    (s) =>
      s.kind === 'run_load' && s.targetId === runId && s.uid === uid && s.finishedAt !== undefined,
  );
  if (!run || (!session && !sent)) {
    return (
      <EmptyState
        title={run ? 'The load scan is not open' : 'This run is not on your phone'}
        description={
          run
            ? 'Open the run and choose “Scan the load and start”.'
            : 'It may be finished, or it is assigned to someone else.'
        }
        action={
          <Link to={run ? `/run/${run.id}` : '/today'}>
            <Button size="touch">{run ? 'Back to the run' : 'Back to Today'}</Button>
          </Link>
        }
      />
    );
  }
  if (!session) return <AlreadyStarted runId={runId} />;
  return <LoadScreen session={session} />;
}

function AlreadyStarted({ runId }: { runId: string }) {
  return (
    <EmptyState
      title="The run has started"
      description="Your load scan was saved."
      action={
        <Link to={`/run/${runId}`}>
          <Button size="touch">Back to the run</Button>
        </Link>
      }
    />
  );
}

function LoadScreen({ session }: { session: OpsSession }) {
  const data = useDriverData();
  const engine = useSessionEngine();
  const ledger = useLedgerById();
  const toast = useToast();
  const navigate = useNavigate();
  const run = data.runs.find((r) => r.id === session.targetId);
  const stops = useMemo(() => data.stopsOf(session.targetId) ?? [], [data, session.targetId]);
  useRunBranch(run?.branchId);
  useWakeLock(true);

  const [camera, setCamera] = useState(false);
  const [last, setLast] = useState<AddScanResult | null>(null);
  const [confirmEmpty, setConfirmEmpty] = useState(false);

  const planned = useMemo(() => plannedBoxes(stops), [stops]);
  const counts = countSession(session, ledger);
  const scanned = new Set(
    session.scans
      .filter((s) => s.status === 'buffered' || s.status === 'queued')
      .map((s) => s.barcode),
  );
  const missing = unscannedPlanned(stops, scanned);
  const missingCount = missing.reduce((n, m) => n + m.barcodes.length, 0);
  const onVan = scanned.size;

  const handleScan = (raw: string): ScanVerdict => {
    const barcode = raw.trim().toUpperCase();
    const result = engine.addScan(session.id, barcode, precheckRunLoad({ barcode, planned }));
    setLast(result);
    return VERDICT[result.tone];
  };

  const start = () => {
    const sentCount = engine.flush(session.id);
    const fresh = engine.get(session.id);
    const command = fresh?.batches[fresh.batches.length - 1];
    if (sentCount > 0 && command) {
      engine.markClosed(session.id, command.commandId);
      toast.show({
        title: 'Run started',
        message: 'Saved. It goes to the server when you have signal.',
        tone: 'success',
      });
      void navigate(`/run/${session.targetId}`);
    } else if (fresh?.lastError) {
      toast.show({ title: 'Not started', message: fresh.lastError, tone: 'danger' });
    }
  };

  if (!run) return null;

  return (
    <div className="ops-stack">
      <BackLink to={`/run/${run.id}`}>The run</BackLink>
      <div>
        <h1 className="ops-title">Load the van</h1>
        <p className="ops-muted" style={{ margin: 0 }}>
          Scan every box as it goes on. Then start the run.
        </p>
      </div>

      <div className="ops-tiles" data-tour="load-counters">
        <Tile n={planned.size} label="planned" />
        <Tile n={onVan} label="scanned" tone="ok" />
        <Tile n={missingCount} label="not scanned" tone={missingCount > 0 ? 'wait' : undefined} />
        {counts.held > 0 ? <Tile n={counts.held} label="held back" tone="bad" /> : null}
      </div>

      {missingCount > 0 ? (
        <div role="alert" className="ops-last" data-tone="warn">
          <strong>
            {missingCount} planned box{missingCount === 1 ? ' is' : 'es are'} not scanned. They stay
            at the branch and their stops will have nothing to deliver.
          </strong>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {missing.slice(0, 5).map((m) => (
              <li key={m.stopId}>
                {m.label}: {m.barcodes.length} box{m.barcodes.length === 1 ? '' : 'es'}
              </li>
            ))}
          </ul>
          {missing.length > 5 ? <div>…and {missing.length - 5} more stops.</div> : null}
        </div>
      ) : null}

      <div data-tour="scan-field">
        <ScanInput
          onScan={(code) => scanFeedback(handleScan(code))}
          status={
            last ? (last.tone === 'bad' ? 'error' : last.tone === 'ok' ? 'ok' : 'idle') : 'idle'
          }
        />
      </div>
      {last ? (
        <div className="ops-last" data-tone={last.tone} role="status">
          <span className="ops-code">{last.barcode}</span> {last.message}
        </div>
      ) : null}
      <Button
        size="touch"
        variant="cta"
        block
        data-tour="scan-camera"
        onClick={() => setCamera(true)}
      >
        Scan with the camera
      </Button>

      {session.lastError ? (
        <p role="alert" className="ops-error">
          {session.lastError}
        </p>
      ) : null}

      <ScanRows
        session={session}
        ledger={ledger}
        onRemove={(b) => engine.removeScan(session.id, b)}
        onRelease={(b) => engine.releaseHeld(session.id, b)}
      />

      <div className="ops-actionbar" data-tour="load-start">
        {onVan > 0 ? (
          <Button size="touch" variant="cta" block onClick={start}>
            Start the run with {onVan} box{onVan === 1 ? '' : 'es'}
          </Button>
        ) : needsLoadScan(stops) ? (
          confirmEmpty ? (
            <Card title="Start with nothing on the van?">
              <div className="ops-stack">
                <p style={{ margin: 0 }}>
                  No box is scanned. Every delivery stop will have nothing to deliver.
                </p>
                <EmptyStart sessionId={session.id} runId={run.id} />
                <Button size="lg" variant="ghost" block onClick={() => setConfirmEmpty(false)}>
                  Keep scanning
                </Button>
              </div>
            </Card>
          ) : (
            <Button size="lg" variant="secondary" block onClick={() => setConfirmEmpty(true)}>
              Start without scanning any box
            </Button>
          )
        ) : (
          <EmptyStart sessionId={session.id} runId={run.id} />
        )}
        {session.scans.length === 0 ? (
          <Button
            size="lg"
            variant="ghost"
            onClick={() => {
              if (engine.discard(session.id)) void navigate(`/run/${run.id}`);
            }}
          >
            Cancel the load scan
          </Button>
        ) : null}
      </div>

      {camera ? (
        <ScannerView
          loadDetector={loadBarcodeDetector}
          title={`Load the van · ${planned.size} planned`}
          count={onVan}
          countLabel="scanned"
          lastResult={
            last ? { code: last.barcode, tone: VERDICT[last.tone], message: last.message } : null
          }
          onRead={handleScan}
          onClose={() => setCamera(false)}
        />
      ) : null}
    </div>
  );
}

/** A run with nothing to load starts with an empty list (04 §4.5: pickup-only runs send none). */
function EmptyStart({ sessionId, runId }: { sessionId: string; runId: string }) {
  const { writer } = useServices();
  const engine = useSessionEngine();
  const toast = useToast();
  const navigate = useNavigate();
  return (
    <Button
      size="touch"
      variant="cta"
      block
      onClick={() => {
        try {
          const sent = writer.send('startRun', { runId, barcodes: [] });
          engine.markClosed(sessionId, sent.id);
          toast.show({
            title: 'Run started',
            message: 'Saved. It goes to the server when you have signal.',
            tone: 'success',
          });
          void navigate(`/run/${runId}`);
        } catch (err) {
          toast.show({
            title: 'Not started',
            message: err instanceof Error ? err.message : String(err),
            tone: 'danger',
          });
        }
      }}
    >
      Start the run
    </Button>
  );
}
